import { useCallback, useEffect, useMemo, useRef } from "react";

import { WARNING_MESSAGE } from "./constants";

interface ComponentProps {
  warn: boolean;
  message?: string;
}

/**
 * Prompts the user when navigating out of the current route with unsaved changes.
 * The pop up will be displayed when the `warn` prop is true (defining when to warn is the
 * responsibility of the implementing component).
 *
 * This component handles two scenarios:
 *  - Navigating to another route inside the app: uses a custom navigation blocker with history API
 *  - Closing the tab/window or reloading the page: uses the browser's default pop up (through the `beforeunload` event)
 *
 * Depending on the browser, when displaying the browser's default box, the message might be defaulted by it, ignoring
 * the one passed by the component.
 *
 * A default message for the component is defined (different from the browser's) but it can be replaced by passing the `message` prop.
 */
const WarnLeaveWithoutSaving = ({ warn, message }: ComponentProps) => {
  const warningMessage = useMemo(() => message || WARNING_MESSAGE, [message]);
  const currentPathRef = useRef<string>(window.location.pathname);

  // Custom navigation blocker for in-app navigation
  const handlePopState = useCallback(
    (event: PopStateEvent) => {
      if (warn && currentPathRef.current !== window.location.pathname) {
        const shouldLeave = window.confirm(warningMessage);
        if (!shouldLeave) {
          // Prevent navigation by pushing the current state back
          window.history.pushState(null, "", currentPathRef.current);
          event.preventDefault();
        } else {
          // Update the current path reference
          currentPathRef.current = window.location.pathname;
        }
      } else {
        // Update the current path reference
        currentPathRef.current = window.location.pathname;
      }
    },
    [warn, warningMessage]
  );

  // Handle beforeunload for tab/window close and page reload
  const beforeUnloadHandler = useCallback(
    (event: BeforeUnloadEvent) => {
      if (warn) {
        event.preventDefault();
        event.returnValue = warningMessage;
        return warningMessage;
      }
    },
    [warn, warningMessage]
  );

  useEffect(() => {
    // Update current path reference when component mounts
    currentPathRef.current = window.location.pathname;

    // Add event listeners
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
  }, [handlePopState, beforeUnloadHandler]);

  // Update current path reference when warn prop changes
  useEffect(() => {
    currentPathRef.current = window.location.pathname;
  }, [warn]);

  return null;
};

export default WarnLeaveWithoutSaving;
